// sd.js - Steel Door Component
// Follows the same pattern as pd.js (Patio Door) and ww.js (Windows)
// Structure: door type selector → expandable sections → drawing area

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { authFetch, authFetchRequest } from '../../utils/api';
import { alertC, confirmC } from '../../components/dialogService';
import { getCookie } from '../../utils/cookies';
import { toPdf, toPdfAuto } from '../../components/toPdf';
import { useCrmSettings } from '../../CRM/SimpleCRM/CrmSettingsContext';
import { get_crm_user_id } from '../../CRM/SimpleCRM/crmUtils';
import { fetchUserField } from '../../utils/userSettings';
import { getCustomerEmail } from '../../utils/QuickDataAccess';
import { createCrmProduct, updateCrmProduct } from '../../CRM/ZohoCrm';

import ExpandableSection from '../../expandablesection';
import ItemSelector from '../../itemselector';
import EmailDialog from '../../records/EmailDialog';
import ViewDetails from '../../docs/viewdetails';

import SmallFloatingNavMenu from '../../windows/wint/components/SmallFloatingNavMenu';
import LargeFloatingNavMenu from '../../windows/wint/components/LargeFloatingNavMenu';
import TopBarNavMenu from '../../windows/wint/components/TopBarNavMenu';
import TopBarToolbarMenu from '../../windows/wint/components/TopBarToolbarMenu';
import SmallFloatingToolbarMenu from '../../windows/wint/components/SmallFloatingToolbarMenu';
import { useThemeColors } from '../../utils/useThemeColors';

import PanelSizingAndFrame from './panels/PanelSizingAndFrame';
import PanelTransom        from './panels/PanelTransom';
import PanelSill           from './panels/PanelSill';
import PanelBrickmould     from './panels/PanelBrickmould';
import PanelDoorpanel      from './panels/PanelDoorpanel';
import PanelGlass          from './panels/PanelGlass';
import Quantity from '../../controls/frame/Quantity';
import Discount from '../../controls/frame/Discount';

import '../../entrydoor.css';
import '../../toolbar.css';
import '../../spinner.css';
import '../../flip.css';

// ============================================
// DOOR TYPES
// ============================================
const DOOR_TYPES = [
  { id: 1, name: 'Single Door',                   imageUrl: '/images/door-1.png' },
  { id: 2, name: 'Single Door - Left Side Lite',  imageUrl: '/images/door-2.png' },
  { id: 3, name: 'Single Door - Right Side Lite', imageUrl: '/images/door-3.png' },
  { id: 4, name: 'Single Door - Side Lites',      imageUrl: '/images/door-4.png' },
  { id: 5, name: 'Double Door',                   imageUrl: '/images/door-5.png' },
  { id: 6, name: 'Double Door - Left Side Lite',  imageUrl: '/images/door-6.png' },
  { id: 7, name: 'Double Door - Right Side Lite', imageUrl: '/images/door-7.png' },
  { id: 8, name: 'Double Door - Side Lites',      imageUrl: '/images/door-8.png' },
];

const DEFAULT_DOCUMENT_TYPE = "steeldoors";
const DEFAULT_ITEM_PATH     = "stdoors/sdt/sd1";

// ============================================
// MAIN COMPONENT
// ============================================
const SD = ({
  DrawComponent = null,
  documentType  = DEFAULT_DOCUMENT_TYPE,
  itemPath      = DEFAULT_ITEM_PATH,
}) => {
  const { settings: crmSettings } = useCrmSettings();
  const crmDefined = crmSettings.external_crm !== 'none';

  // ============================================
  // UI STATE
  // ============================================
  const [showOriginalToolbar,       setShowOriginalToolbar]       = useState(false);
  const [showOnlySelectedSection,   setShowOnlySelectedSection]   = useState(false);
  const [enableFloatingNavMenu,     setEnableFloatingNavMenu]     = useState(true);
  const [enableFloatingToolbarMenu, setEnableFloatingToolbarMenu] = useState(true);
  const [useSmallNavMenu,           setUseSmallNavMenu]           = useState(true);
  // Docked top-bar nav+toolbar vs floating menus, driven by the user's
  // navMenuStyle preference (Home → Preferences). Top bar is the default.
  const [showTopBar,                setShowTopBar]                = useState(true);
  const [selectedSectionId,         setSelectedSectionId]         = useState('DoorType');
  const [expandedSections,          setExpandedSections]          = useState({ DoorType: true });
  const tc = useThemeColors();
  const [showAltToolbar,            setShowAltToolbar]            = useState(false);
  const [zoom,                      setZoom]                      = useState(1.0);
  const [flipped,                   setFlipped]                   = useState(false);

  // ============================================
  // NAVIGATION & ROUTING
  // ============================================
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ============================================
  // QUOTE / ITEM STATE
  // ============================================
  const [quoteId,        setQuoteId]        = useState();
  const [quoteItemId,    setQuoteItemId]    = useState();
  const [fkCustomerId,   setFkCustomerId]   = useState();
  const [fkUserId,       setFkUserId]       = useState();
  const [viewItem,       setViewItem]       = useState();
  const [reference_no,   setReferenceNo]    = useState();
  const [newQuoteItemId, setNewQuoteItemId] = useState(null);
  const [lastDateSaved,  setLastDateSaved]  = useState("NA");
  const [isDisabled,     setIsDisabled]     = useState(false);

  // ============================================
  // DATA STATE
  // ============================================
  const [doorData,             setDoorData]             = useState({ document: {}, frameData: {}, glass: {}, finish: {}, hardware: {}, doorPanel: {}, sidelite: {}, transom: {}, installation: {}, notes: {}, discounts: {}, calc: {} });
  const [initialDoorDataJSON,  setInitialDoorDataJSON]  = useState(null);
  const [isViewDetailsVisible, setIsViewDetailsVisible] = useState(false);
  const [viewDetailsData,      setViewDetailsData]      = useState([]);
  const [hasLoaded,            setHasLoaded]            = useState(false);
  const [displayPrices,        setDisplayPrices]        = useState(false);
  const [isLoading,            setIsLoading]            = useState(false);

  // ============================================
  // DOOR TYPE SELECTOR
  // ============================================
  const [selectedDoor, setSelectedDoor] = useState(null);

  // ============================================
  // FRAME / SIZING  — owned by PanelSizingAndFrame, stored here as a slice
  // ============================================
  const [frameData,      setFrameData]      = useState({ doorSize: { value: '32"', label: '32"' }, doorHeight: { value: "6'8\"", label: "6'8\"" } });

  // ============================================
  // TRANSOM — owned by PanelTransom
  // ============================================
  const [transomData,    setTransomData]    = useState({});

  // ============================================
  // SILL — owned by PanelSill
  // ============================================
  const [sillData,       setSillData]       = useState({});

  // ============================================
  // BRICKMOULD — owned by PanelBrickmould
  // ============================================
  const [brickmouldData, setBrickmouldData] = useState({});

  // ============================================
  // DOOR PANEL
  // ============================================
  const [doorPanelValue,     setDoorPanelValue]     = useState({ value: "", label: "" });
  const [doorPanelColor,     setDoorPanelColor]     = useState({ value: "", label: "" });
  const [doorPanelData,      setDoorPanelData]      = useState({});

  // ============================================
  // GLASS / DOOR LITE (PanelGlass)
  // ============================================
  const [glassData,           setGlassData]           = useState({});

  // Legacy glass state (kept for backward-compat with saved doorData)
  const [hasDoorLite,         setHasDoorLite]         = useState(false);   // depends on selected panel
  const [doorLiteValue,       setDoorLiteValue]       = useState({ value: "", label: "" });
  const [glazingTypeValue,    setGlazingTypeValue]    = useState({ value: "Double", label: "Double" });
  const [glassEnergyValue,    setGlassEnergyValue]    = useState({ value: "LowE 272", label: "LowE 272" });
  const [glassStrengthValue,  setGlassStrengthValue]  = useState({ value: "", label: "" });
  const [glassDividersValue,  setGlassDividersValue]  = useState({ value: "None", label: "None" });

  // ============================================
  // FINISH
  // ============================================
  const [exteriorColorValue,  setExteriorColorValue]  = useState({ value: "White", label: "White" });
  const [interiorColorValue,  setInteriorColorValue]  = useState({ value: "White", label: "White" });

  // ============================================
  // HARDWARE
  // ============================================
  const [handlesetValue,      setHandlesetValue]      = useState({ value: "", label: "" });
  const [handlesetFinish,     setHandlesetFinish]     = useState({ value: "", label: "" });
  const [deadboltValue,       setDeadboltValue]       = useState({ value: "No", label: "No" });
  const [multipointPrep,      setMultipointPrep]      = useState({ value: "No", label: "No" });
  const [weatherStripValue,   setWeatherStripValue]   = useState({ value: "", label: "" });

  // ============================================
  // SIDELITE
  // ============================================
  const [sideliteSizeValue,   setSideliteSizeValue]   = useState({ value: "", label: "" });
  const [sideliteGlass,       setSideliteGlass]       = useState({ value: "", label: "" });

  // ============================================
  // TRANSOM
  // ============================================
  const [transomValue,        setTransomValue]        = useState({ value: "No", label: "No" });
  const [transomGlass,        setTransomGlass]        = useState({ value: "", label: "" });
  const [overallHeight,       setOverallHeight]       = useState({ value: "", label: "" });

  // ============================================
  // INSTALLATION
  // ============================================
  const [installationTypeValue, setInstallationTypeValue] = useState({ value: "None", label: "None" });
  const [brickmoldValue,        setBrickmoldValue]        = useState({ value: "None", label: "None" });
  const [brickmoldColorValue,   setBrickmoldColorValue]   = useState({ value: "White", label: "White" });
  const [sillNoseValue,         setSillNoseValue]         = useState({ value: "None", label: "None" });

  // ============================================
  // NOTES / DISCOUNTS
  // ============================================
  const [locationTagValue,    setLocationTagValue]    = useState("");
  const [notesValue,          setNotesValue]          = useState("");
  const [quantityValue,       setQuantityValue]       = useState("1");
  const [discountValue,       setDiscountValue]       = useState(0);

  // ============================================
  // CRM STATE
  // ============================================
  const [crm_product_id,        set_crm_product_id]        = useState();
  const [crmUserId,             setCrmUserId]              = useState(false);
  const [ctrl_discount_enabled, set_ctrl_discount_enabled] = useState(0);

  // ============================================
  // EMAIL STATE
  // ============================================
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSending,       setIsSending]       = useState(false);
  const [customerEmail,   setCustomerEmail]   = useState("");

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const init = async () => {
      const id = await get_crm_user_id();
      setCrmUserId(id);

      const ctrl_discount_status = await fetchUserField('ctrl_discount_enabled');
      set_ctrl_discount_enabled(ctrl_discount_status);

      const navMenuPref = await fetchUserField('enableFloatingNavMenu');
      setEnableFloatingNavMenu(navMenuPref ?? true);
      const toolbarMenuPref = await fetchUserField('enableFloatingToolbarMenu');
      setEnableFloatingToolbarMenu(toolbarMenuPref ?? true);
      const navStylePref = await fetchUserField('navMenuStyle');
      setShowTopBar(navStylePref !== 'floating');

      const crmProductId = searchParams.get("id") || "NA";
      let inputData = await authFetch(`/local/get-ww-input-data/${crmProductId}`);

      if (inputData?.quoteItemId) {
        setQuoteId(inputData.quoteId);
        setQuoteItemId(inputData.quoteItemId);
        setFkCustomerId(inputData.fkCustomerId);
        setFkUserId(inputData.fkUserId);
        setReferenceNo(inputData.reference_no);
      } else if (location.state) {
        const { quoteId, quoteItemId, fkCustomerId, fkUserId, viewItem, reference_no } = location.state;
        setQuoteId(quoteId);
        setQuoteItemId(quoteItemId);
        setFkCustomerId(fkCustomerId);
        setViewItem(viewItem);
        setFkUserId(fkUserId);
        setReferenceNo(reference_no);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!fkCustomerId) return;
    const loadEmail = async () => {
      const email = await getCustomerEmail(fkCustomerId);
      setCustomerEmail(email || "");
    };
    loadEmail();
  }, [fkCustomerId]);

  // ============================================
  // LOAD SAVED DATA
  // ============================================
  useEffect(() => {
    if (viewItem === 'readonly') {
      setIsDisabled(true);
      setDisplayPrices(true);
    }

    const fetchData = async () => {
      if (!quoteId || !quoteItemId) return;
      try {
        const data = await authFetch(`/itemquotes/${quoteItemId}`);
        if (!data?.doorData) {
          await alertC("No valid doorData found.");
          return;
        }
        setDoorData(data.doorData);
        if (crmDefined) set_crm_product_id(data?.crm_product_id || null);
        setFormData(data.doorData);
        setInitialDoorDataJSON(JSON.stringify(data.doorData));
        setIsViewDetailsVisible(true);
        setHasLoaded(true);
      } catch (error) {
        console.error("Fetch error:", error);
        await alertC(`Error: ${error.message}`);
      }
    };
    fetchData();
  }, [quoteId, quoteItemId]);

  // ============================================
  // SET FORM DATA FROM LOADED DATA
  // ============================================
  const setFormData = (d) => {
    if (!d) return;
    setSelectedDoor(d.frameData?.doorType || null);
    // frameData slice is passed directly into PanelSizingAndFrame via the data prop
    setFrameData(d.frameData || {});

    setDoorPanelValue(d.doorPanel?.panel         || { value: "", label: "" });
    setDoorPanelColor(d.doorPanel?.color         || { value: "", label: "" });
    setDoorPanelData(d.doorPanelData             || {});

    setGlassData(d.glassData                     || {});

    setHasDoorLite(d.glass?.hasDoorLite          || false);
    setDoorLiteValue(d.glass?.doorLite           || { value: "", label: "" });
    setGlazingTypeValue(d.glass?.glazingType     || { value: "Double", label: "Double" });
    setGlassEnergyValue(d.glass?.glassEnergy     || { value: "LowE 272", label: "LowE 272" });
    setGlassStrengthValue(d.glass?.glassStrength || { value: "", label: "" });
    setGlassDividersValue(d.glass?.glassDividers || { value: "None", label: "None" });

    setExteriorColorValue(d.finish?.exteriorColor || { value: "White", label: "White" });
    setInteriorColorValue(d.finish?.interiorColor || { value: "White", label: "White" });

    setHandlesetValue(d.hardware?.handleset      || { value: "", label: "" });
    setHandlesetFinish(d.hardware?.handlesetFinish || { value: "", label: "" });
    setDeadboltValue(d.hardware?.deadbolt        || { value: "No", label: "No" });
    setMultipointPrep(d.hardware?.multipointPrep || { value: "No", label: "No" });
    setWeatherStripValue(d.hardware?.weatherStrip || { value: "", label: "" });

    setSideliteSizeValue(d.sidelite?.size        || { value: "", label: "" });
    setSideliteGlass(d.sidelite?.glass           || { value: "", label: "" });

    setTransomValue(d.transom?.transom           || { value: "No", label: "No" });
    setTransomGlass(d.transom?.glass             || { value: "", label: "" });
    setOverallHeight(d.transom?.overallHeight    || { value: "", label: "" });
    setTransomData(d.transomData                 || {});

    setSillData(d.sillData                       || {});
    setBrickmouldData(d.brickmouldData           || {});

    setInstallationTypeValue(d.installation?.installationType || { value: "None", label: "None" });
    setBrickmoldValue(d.installation?.brickmold   || { value: "None", label: "None" });
    setBrickmoldColorValue(d.installation?.brickmoldColor || { value: "White", label: "White" });
    setSillNoseValue(d.installation?.sillNose    || { value: "None", label: "None" });

    setLocationTagValue(d.notes?.locationTag     || "");
    setNotesValue(d.notes?.notes                 || "");
    setQuantityValue(d.discounts?.quantity       || "1");
    setDiscountValue(d.discounts?.discount       || 0);
  };

  // ============================================
  // BUILD doorData OBJECT (for save)
  // ============================================
  useEffect(() => {
    setDoorData({
      document: { orderDate: lastDateSaved },
      frameData: {
        ...frameData,
        doorType: selectedDoor,
        name:     selectedDoor?.name || "Steel Door",
      },
      doorPanel: {
        panel: doorPanelValue,
        color: doorPanelColor,
      },
      doorPanelData,
      glassData,
      glass: {
        hasDoorLite:   hasDoorLite,
        doorLite:      doorLiteValue,
        glazingType:   glazingTypeValue,
        glassEnergy:   glassEnergyValue,
        glassStrength: glassStrengthValue,
        glassDividers: glassDividersValue,
      },
      finish: {
        exteriorColor: exteriorColorValue,
        interiorColor: interiorColorValue,
      },
      hardware: {
        handleset:      handlesetValue,
        handlesetFinish: handlesetFinish,
        deadbolt:       deadboltValue,
        multipointPrep: multipointPrep,
        weatherStrip:   weatherStripValue,
      },
      sidelite: {
        size:  sideliteSizeValue,
        glass: sideliteGlass,
      },
      transom: {
        transom:       transomValue,
        glass:         transomGlass,
        overallHeight: overallHeight,
      },
      transomData,
      sillData,
      brickmouldData,
      installation: {
        installationType: installationTypeValue,
        brickmold:        brickmoldValue,
        brickmoldColor:   brickmoldColorValue,
        sillNose:         sillNoseValue,
      },
      notes: {
        locationTag: locationTagValue,
        notes:       notesValue,
      },
      discounts: {
        quantity: quantityValue,
        discount: discountValue,
      },
    });
  }, [
    selectedDoor, frameData,
    doorPanelValue, doorPanelColor, doorPanelData, glassData,
    hasDoorLite, doorLiteValue, glazingTypeValue, glassEnergyValue, glassStrengthValue, glassDividersValue,
    exteriorColorValue, interiorColorValue,
    handlesetValue, handlesetFinish, deadboltValue, multipointPrep, weatherStripValue,
    sideliteSizeValue, sideliteGlass,
    transomValue, transomGlass, overallHeight,
    transomData, sillData, brickmouldData,
    installationTypeValue, brickmoldValue, brickmoldColorValue, sillNoseValue,
    locationTagValue, notesValue, quantityValue, discountValue,
    lastDateSaved,
  ]);

  // ============================================
  // ACTION HANDLERS
  // ============================================
  const handleFlipToggle  = () => setFlipped(prev => !prev);
  const handleZoomIn      = () => setZoom(prev => Math.min(prev + 0.05, 2));
  const handleZoomOut     = () => setZoom(prev => Math.max(prev - 0.05, 0.2));
  const handleSendEmail   = () => setShowEmailDialog(true);
  const toggleViewDetails = () => setIsViewDetailsVisible(prev => !prev);
  const togglePrices      = () => setDisplayPrices(prev => !prev);

  const handlePdfDownload = async () => {
    setIsLoading(true);
    try {
      // toPdfAuto picks server-side vs legacy based on USE_PDF_SERVER (constants.js).
      await toPdfAuto({
        container: ".view-details",
        filename: `${selectedDoor?.name || "SteelDoor"}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // SAVE QUOTE HANDLER
  // ============================================
  const handleSaveQuote = async (bSaveAndBack = false) => {
    try {
      if (bSaveAndBack) {
        const currentDoorDataJSON = JSON.stringify(doorData);
        const hasDataChanged = initialDoorDataJSON && (currentDoorDataJSON !== initialDoorDataJSON);
        if (hasDataChanged || !Number.isInteger(Number(quoteItemId))) {
          const userChoice = await confirmC("Do you want to save before exiting?");
          if (!userChoice) {
            navigate('/quotes', { state: { quoteId, quoteItemId } });
            return;
          }
        } else {
          navigate('/quotes', { state: { quoteId, quoteItemId } });
          return;
        }
      }

      const userId = getCookie('user_id');
      if (parseInt(userId) !== parseInt(fkUserId)) {
        await alertC("This quote item doesn't belong to you, you can't modify and save it.");
        if (bSaveAndBack) navigate('/quotes', { state: { quoteId, quoteItemId } });
        return;
      }

      const date = new Date();
      const formattedDate = date.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });
      setLastDateSaved(formattedDate);
      doorData.document.orderDate = formattedDate;

      if (quoteId && !quoteItemId && !newQuoteItemId) {
        const payload = { fkQuoteId: quoteId, doorData, itemType: itemPath };
        const itemRow = await authFetchRequest(
          `/itemquotes/`, payload, "POST",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );
        setNewQuoteItemId(itemRow?.id);
        if (crmDefined && crmUserId !== "NA") {
          const product_id = await createCrmProduct({
            quoteId, itemRow, doorData,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
          if (product_id) set_crm_product_id(product_id);
        }
      }

      if ((quoteId && quoteItemId) || (quoteId && newQuoteItemId)) {
        const currentQuoteItemId = quoteItemId || newQuoteItemId;
        const payload = { fkQuoteId: quoteId, doorData };
        const itemRow = await authFetchRequest(
          `/itemquotes/${currentQuoteItemId}`, payload, "PUT",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );
        if (crmDefined && crmUserId !== "NA") {
          await updateCrmProduct({
            quoteId, itemRow, doorData, crm_product_id,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
        }
      }

      if (bSaveAndBack) navigate('/quotes', { state: { quoteId, quoteItemId } });
    } catch (err) {
      console.error("Failed to save:", err);
      await alertC("Failed to save.");
    }
  };

  // ============================================
  // FLOATING NAV HELPERS
  // ============================================
  const scrollToSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setTimeout(() => {
      const el = document.getElementById(`section-${sectionId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleSectionExpandChange = (sectionId, isExpanded) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: isExpanded }));
  };

  const isSectionVisible = (sectionId) => {
    if (!showOnlySelectedSection) return true;
    return selectedSectionId === sectionId;
  };

  const getForceExpanded = (sectionId) => {
    if (showOnlySelectedSection && selectedSectionId === sectionId) return true;
    return null;
  };

  const updateCalcData = (newCalc) => {
    setDoorData(prev => ({ ...prev, calc: { ...prev.calc, ...newCalc } }));
  };

  // ============================================
  // CONTROL MAP
  // Controls will be added here as individual
  // components are built under stdoors/controls/
  // ============================================
  const controlMap = [
    {
      id: 'Quantity',
      component: <Quantity value={quantityValue} onChange={setQuantityValue} disabled={isDisabled} />,
      position: [1, 1],
      status: 'active',
      expandableSectionId: 'Discounts',
    },
    {
      id: 'Discount',
      component: <Discount value={discountValue} onChange={setDiscountValue} disabled={isDisabled || !ctrl_discount_enabled} />,
      position: [1, 2],
      status: 'active',
      expandableSectionId: 'Discounts',
    },
  ];

  // ============================================
  // SECTION DEFINITIONS
  // ============================================
  const sections = [
    { id: 'SizingAndFrame',   title: '📐 Sizing & Frame',        panel: 'PanelSizingAndFrame' },
    { id: 'DoorPanel',        title: '🚪 Door Panel',            panel: 'PanelDoorpanel' },
    { id: 'Glass',            title: '🔲 Glass / Door Lite',     panel: null },
    { id: 'Finish',           title: '🎨 Finish',                panel: null },
    { id: 'Hardware',         title: '🔑 Hardware',              panel: null },
    { id: 'Sidelite',         title: '◧ Side Lite',             panel: null },
    { id: 'Transom',          title: '🪟 Transom',               panel: 'PanelTransom' },
    { id: 'Sill',             title: '🪵 Sill',                  panel: 'PanelSill' },
    { id: 'Brickmould',       title: '🧱 Brickmould',            panel: 'PanelBrickmould' },
    { id: 'Installation',     title: '🔧 Installation',          panel: null },
    { id: 'AdditionalItems',  title: '➕ Additional Items',       panel: null },
    { id: 'Notes',            title: '📝 Notes',                 panel: null },
    { id: 'Discounts',        title: '🏷️ Quantity & Discount',   panel: null },
  ];

  const disabledSections = {};
  const hiddenSections   = [];

  // Standard doors have their own section set, so the docked top-bar nav uses
  // these labels (DoorType first, then the section list above).
  const navMenuItems = [
    { id: 'DoorType',        label: 'Door Type' },
    { id: 'SizingAndFrame',  label: 'Sizing & Frame' },
    { id: 'DoorPanel',       label: 'Door Panel' },
    { id: 'Glass',           label: 'Glass / Door Lite' },
    { id: 'Finish',          label: 'Finish' },
    { id: 'Hardware',        label: 'Hardware' },
    { id: 'Sidelite',        label: 'Side Lite' },
    { id: 'Transom',         label: 'Transom' },
    { id: 'Sill',            label: 'Sill' },
    { id: 'Brickmould',      label: 'Brickmould' },
    { id: 'Installation',    label: 'Installation' },
    { id: 'AdditionalItems', label: 'Additional Items' },
    { id: 'Notes',           label: 'Notes' },
    { id: 'Discounts',       label: 'Quantity & Discount' },
  ];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className='entire-container' style={showTopBar ? { marginTop: '-1.5rem' } : undefined}>

      {/* Floating Nav Menu */}
      {enableFloatingNavMenu && !showTopBar && useSmallNavMenu && (
        <SmallFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(false)}
        />
      )}
      {enableFloatingNavMenu && !showTopBar && !useSmallNavMenu && (
        <LargeFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(true)}
        />
      )}

      {/* Floating Toolbar Menu */}
      {enableFloatingToolbarMenu && !showTopBar && (
        <SmallFloatingToolbarMenu
          isViewDetailsVisible={isViewDetailsVisible}
          toggleViewDetails={toggleViewDetails}
          displayPrices={displayPrices}
          togglePrices={togglePrices}
          isLoading={isLoading}
          handlePdfDownload={handlePdfDownload}
          handleSendEmail={handleSendEmail}
          handleSaveQuote={handleSaveQuote}
          flipped={flipped}
          handleFlipToggle={handleFlipToggle}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          showOriginalToolbar={showOriginalToolbar}
          toggleOriginalToolbar={() => setShowOriginalToolbar(prev => !prev)}
        />
      )}

      <div className={`door-container-left ${showTopBar ? '' : 'shift-down'}`}>

        {/* Original Toolbar (fallback) */}
        {(showOriginalToolbar || !enableFloatingToolbarMenu) && (
          <div className="toolbar-container">
            <div className="toolbar">
              <button className="toolbar-button" onClick={toggleViewDetails}>
                {isViewDetailsVisible ? "Hide Details" : "View Details"}
              </button>
              <button className="toolbar-button" onClick={togglePrices} disabled={!isViewDetailsVisible}>
                {displayPrices ? "Hide Prices" : "Show Prices"}
              </button>
              <button
                className={`toolbar-button ${isLoading ? "loading" : ""}`}
                onClick={handlePdfDownload}
                disabled={!isViewDetailsVisible || isLoading}
              >
                {isLoading ? <span className="spinner"></span> : "PDF"}
              </button>
              <button className="toolbar-button" onClick={handleSendEmail} disabled={!isViewDetailsVisible}>Email</button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(false)}>Save</button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(true)}>Exit</button>
              <span className="arrow" onClick={() => setShowAltToolbar(prev => !prev)} title="Toggle more tools">
                {showAltToolbar ? "▲" : "▼"}
              </span>
            </div>
            {showAltToolbar && (
              <div className="toolbar toolbar-secondary">
                <button className="toolbar-button" onClick={handleFlipToggle}>{flipped ? 'Show Front' : 'Show Back'}</button>
                <button className="toolbar-button" onClick={handleZoomIn}>Zoom In</button>
                <button className="toolbar-button" onClick={handleZoomOut}>Zoom Out</button>
              </div>
            )}
          </div>
        )}

        {/* Door Name + docked top bars */}
        {showTopBar && (enableFloatingToolbarMenu || enableFloatingNavMenu) ? (
          // Caption lives inside the sticky header so the docked bars never
          // cover it on scroll; the whole block stays pinned at the top.
          <div style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: tc.panelBg, paddingBottom: '6px' }}>
            <div className="door">
              <p><b><i>{selectedDoor?.name || "Steel Door"}</i></b></p>
            </div>
            {enableFloatingToolbarMenu && (
              <TopBarToolbarMenu
                isViewDetailsVisible={isViewDetailsVisible}
                toggleViewDetails={toggleViewDetails}
                displayPrices={displayPrices}
                togglePrices={togglePrices}
                isLoading={isLoading}
                handlePdfDownload={handlePdfDownload}
                handleSendEmail={handleSendEmail}
                handleSaveQuote={handleSaveQuote}
                flipped={flipped}
                handleFlipToggle={handleFlipToggle}
                handleZoomIn={handleZoomIn}
                handleZoomOut={handleZoomOut}
              />
            )}
            {enableFloatingNavMenu && (
              <TopBarNavMenu
                menuItems={navMenuItems}
                selectedSectionId={selectedSectionId}
                showOnlySelected={showOnlySelectedSection}
                disabledSections={disabledSections}
                hiddenSections={hiddenSections}
                onSelectSection={(id) => {
                  setShowOnlySelectedSection(true);
                  setSelectedSectionId(id);
                  setExpandedSections(prev => ({ ...prev, [id]: true }));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onViewAll={() => {
                  setShowOnlySelectedSection(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            )}
          </div>
        ) : (
          /* Door Name */
          <div className="door">
            <p><b><i>{selectedDoor?.name || "Steel Door"}</i></b></p>
          </div>
        )}

        {/* Door Type Selector — always visible at top */}
        <ExpandableSection
          title="🚪 Select Door Type"
          sectionId="DoorType"
          state={expandedSections['DoorType'] || true}
          onExpandChange={handleSectionExpandChange}
          forceExpanded={getForceExpanded('DoorType')}
          controlMap={[]}
          panelLocation="2"
        >
          <ItemSelector
            items={DOOR_TYPES}
            selectedDoor={selectedDoor}
            onSelect={(door) => {
              setSelectedDoor(door);
              setExpandedSections(prev => ({ ...prev, DoorType: false, SizingAndFrame: true }));
              setSelectedSectionId('SizingAndFrame');
            }}
          />
        </ExpandableSection>

        {/* Expandable Sections — only shown after door type is selected */}
        {selectedDoor && (
          <>
            {/* Panel components — each owns its section's controlMap */}
            {isSectionVisible('SizingAndFrame') && (
              <PanelSizingAndFrame
                data={frameData}
                onChange={setFrameData}
                selectedDoor={selectedDoor}
                disabled={isDisabled}
                sectionId="SizingAndFrame"
                state={expandedSections['SizingAndFrame'] ?? true}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('SizingAndFrame')}
              />
            )}

            {/* Transom panel */}
            {isSectionVisible('Transom') && (
              <PanelTransom
                data={transomData}
                onChange={setTransomData}
                selectedDoor={selectedDoor}
                disabled={isDisabled}
                sectionId="Transom"
                state={expandedSections['Transom'] ?? false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('Transom')}
                frameHeight={frameData?.frameHeight}
              />
            )}

            {/* Sill panel */}
            {isSectionVisible('Sill') && (
              <PanelSill
                data={sillData}
                onChange={setSillData}
                disabled={isDisabled}
                sectionId="Sill"
                state={expandedSections['Sill'] ?? false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('Sill')}
              />
            )}

            {/* Brickmould panel */}
            {isSectionVisible('Brickmould') && (
              <PanelBrickmould
                data={brickmouldData}
                onChange={setBrickmouldData}
                disabled={isDisabled}
                sectionId="Brickmould"
                state={expandedSections['Brickmould'] ?? false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('Brickmould')}
                frameWidth={frameData?.frameWidth}
                frameHeight={frameData?.frameHeight}
              />
            )}

            {/* Door Panel */}
            {isSectionVisible('DoorPanel') && (
              <PanelDoorpanel
                data={doorPanelData}
                onChange={setDoorPanelData}
                disabled={isDisabled}
                sectionId="DoorPanel"
                state={expandedSections['DoorPanel'] ?? false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('DoorPanel')}
                doorSize={frameData?.doorSize}
                doorHeight={frameData?.doorHeight}
              />
            )}

            {/* Glass / Door Lite — visible only when selected panel has doorlite=1 */}
            {isSectionVisible('Glass') && doorPanelData?.selectedPanel?.doorlite === '1' && (
              <PanelGlass
                data={glassData}
                onChange={setGlassData}
                disabled={isDisabled}
                sectionId="Glass"
                state={expandedSections['Glass'] ?? false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded('Glass')}
                selectedPanel={doorPanelData?.selectedPanel}
              />
            )}

            {/* Remaining sections (empty until panels are built) */}
            {sections.filter(s => !['SizingAndFrame', 'Transom', 'Sill', 'Brickmould', 'DoorPanel', 'Glass'].includes(s.id)).map(section => {
              const sectionControls = controlMap.filter(c => c.expandableSectionId === section.id);
              return isSectionVisible(section.id) && (
                <ExpandableSection
                  key={section.id}
                  title={section.title}
                  panelLocation="2"
                  controlMap={sectionControls}
                  sectionId={section.id}
                  state={expandedSections[section.id] || false}
                  onExpandChange={handleSectionExpandChange}
                  forceExpanded={getForceExpanded(section.id)}
                />
              );
            })}
          </>
        )}

      </div>

      {/* Drawing Area */}
      <div className={`door-container ${showTopBar ? '' : 'shift-down'} image-container1 ` + (isViewDetailsVisible ? "hidden-left-panel" : "")}>
        <div className="fixed-container_new">
          <div className={`svg-flip-container ${flipped ? 'flipped' : ''}`}>
            {DrawComponent ? (
              <DrawComponent doorData={doorData} zoom={zoom} flip={flipped} />
            ) : (
              <div style={{
                width: 400, height: 500,
                border: '2px dashed #ccc',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#f9f9f9'
              }}>
                <p style={{ color: '#999' }}>Steel Door Drawing (Coming Soon)</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Details Sliding Panel — only rendered when loaded from DB */}
      {isViewDetailsVisible && (
        <ViewDetails
          isVisible={isViewDetailsVisible}
          data={viewDetailsData}
          doorData={doorData}
          updateCalcData={updateCalcData}
          zoom={zoom}
          drawing={
            <div className={`svg-flip-container ${flipped ? 'flipped' : ''}`} style={{ display: 'inline-block' }}>
              {DrawComponent ? (
                <DrawComponent doorData={doorData} zoom={zoom} />
              ) : (
                <div style={{ width: 300, height: 400, border: '1px solid #ccc' }}>Drawing Placeholder</div>
              )}
            </div>
          }
        />
      )}

      {/* Email Dialog */}
      {showEmailDialog && (
        <EmailDialog
          isOpen={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          isSending={isSending}
          setIsSending={setIsSending}
          customerEmail={customerEmail}
        />
      )}

    </div>
  );
};

export default SD;
